import { toast } from 'react-toastify';
import { apiCall } from '~/providers';
import { MultipleConfigOption, TableRow } from '~/components/NewTable/types';
import { SetpointSelection } from './types';
import { RemoteControlSetpointFields } from './components/RemoteControlSetpointFields';
import {
  ALLOWED_SETPOINT_RANGE,
  areRemoteControlSetpointsInRange,
  areRemoteControlSetpointsNumeric,
  isRemoteControlSetpointRangeValid,
  remoteControlSetpointOf,
} from '../FormEditMachine/helpers';

export const REMOTE_CONTROL_SETPOINTS_OPTION = 'setRemoteControlSetpoints';

const MACHINES_PER_REQUEST = 60;

function selectionOf(selected: unknown): SetpointSelection {
  const selection = selected as SetpointSelection | undefined;

  return { minimum: selection?.minimum ?? null, maximum: selection?.maximum ?? null };
}

export async function applyRemoteControlSetpoints(
  machines: TableRow[],
  selected: unknown,
  t: (key: string, values?: Record<string, unknown>) => string,
): Promise<boolean> {
  const { minimum, maximum } = selectionOf(selected);
  const minimumSetpoint = remoteControlSetpointOf(minimum);
  const maximumSetpoint = remoteControlSetpointOf(maximum);

  if (minimumSetpoint === null || maximumSetpoint === null) {
    toast.warn(t('informeOsDoisSetpoints'));
    return false;
  }
  if (!areRemoteControlSetpointsNumeric(minimum, maximum)) {
    toast.warn(t('setpointDeveSerUmNumero'));
    return false;
  }
  if (!areRemoteControlSetpointsInRange(minimum, maximum)) {
    toast.warn(t('setpointForaDaFaixa', ALLOWED_SETPOINT_RANGE));
    return false;
  }
  if (!isRemoteControlSetpointRangeValid(minimum, maximum)) {
    toast.warn(t('setpointMinimoDeveSerMenorQueOMaximo'));
    return false;
  }

  const nameById = new Map(machines.map((machine) => [machine.GROUP_ID as number, machine.GROUP_NAME as string]));
  const machineIds = [...nameById.keys()];
  const blocks: number[][] = [];
  for (let i = 0; i < machineIds.length; i += MACHINES_PER_REQUEST) {
    blocks.push(machineIds.slice(i, i + MACHINES_PER_REQUEST));
  }

  const results = await Promise.allSettled(
    blocks.map((block) => apiCall('/clients/multiple-configs-machines', {
      MACHINE_IDS: block,
      TYPE: REMOTE_CONTROL_SETPOINTS_OPTION,
      REMOTE_CONTROL_MINIMUM_SETPOINT: minimumSetpoint,
      REMOTE_CONTROL_MAXIMUM_SETPOINT: maximumSetpoint,
    })),
  );

  const applied: number[] = [];
  const notApplied: number[] = [];
  results.forEach((result, index) => {
    if (result.status === 'fulfilled') {
      applied.push(...result.value.applied);
      notApplied.push(...result.value.ignored.map((item) => item.MACHINE_ID));
    } else {
      notApplied.push(...blocks[index]);
    }
  });

  if (notApplied.length) {
    const names = notApplied.map((machineId) => nameById.get(machineId) ?? machineId).join(', ');
    toast.warn(t('resultadoSetpointsEmLote', { applied: applied.length, naoAtualizadas: names }));
  } else {
    toast.success(t('maquinasAlteradasComSucesso'));
  }

  return true;
}

export function remoteControlSetpointsOption(t: (key: string) => string): MultipleConfigOption {
  return {
    value: REMOTE_CONTROL_SETPOINTS_OPTION,
    name: t('definirSetpointsMinMaxControleInteligente'),
    modal: {
      kind: 'custom',
      name: t('setpointsDoControleInteligente'),
      render: ({ selected, setSelected }) => (
        <RemoteControlSetpointFields selection={selectionOf(selected)} onChange={setSelected} t={t} />
      ),
    },
  };
}
